import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import { generateSlideFromPrompt, generateSlideFromScript } from '../utils/slideGenerator.js'
import { useProjects } from '../hooks/useProjects'
import { useAuth } from '../contexts/AuthContext'
import creditService from '../lib/creditService'
import CreditDisplay from '../components/Credits/CreditDisplay'
import { supabase } from '../lib/supabase.js'
import { imageService } from '../lib/imageService.js'
import aiService from '../lib/aiService.js'
import AdvancedForm from '../components/AdvancedForm'
import * as FiIcons from 'react-icons/fi'

const { FiFileText, FiZap, FiUpload, FiArrowRight, FiCheck } = FiIcons

const CreateProject = () => {
  const navigate = useNavigate()
  const { createProject } = useProjects()
  const { user } = useAuth()
  const [selectedOption, setSelectedOption] = useState(null)
  const [prompt, setPrompt] = useState('')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [projectTitle, setProjectTitle] = useState('')
  const [progressStep, setProgressStep] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const [estimateSeconds, setEstimateSeconds] = useState(0)
  const estimateTimerRef = React.useRef(null)
  const [credits, setCredits] = useState(null)
  const [creditsLoading, setCreditsLoading] = useState(true)
  const [advancedData, setAdvancedData] = useState({})

  React.useEffect(() => {
    if (user) {
      loadCredits()
    }
  }, [user])

  const loadCredits = async () => {
    try {
      setCreditsLoading(true)
      const creditData = await creditService.getUserCredits(user.id)
      setCredits(creditData)
    } catch (error) {
      console.error('Error loading credits:', error)
    } finally {
      setCreditsLoading(false)
    }
  }

  const options = [
    {
      id: 'blank',
      title: 'Blank Deck',
      description: 'Start with 3 empty slides and build from scratch',
      icon: FiFileText,
      color: 'blue',
      features: ['3 starter slides', 'Full customization', 'Drag & drop editor']
    },
    {
      id: 'prompt',
      title: 'AI Generated',
      description: 'Describe your project and let AI create the deck',
      icon: FiZap,
      color: 'purple',
      features: ['AI-powered content', 'Professional templates', 'Smart slide structure']
    },
    {
      id: 'script',
      title: 'From Script',
      description: 'Upload a script file and generate slides automatically',
      icon: FiUpload,
      color: 'green',
      features: ['Script analysis', 'Auto slide generation', 'Content extraction']
    }
  ]

  const createSlidesInDatabase = async (projectId, slidesData) => {
    console.log('🔍 Creating slides in database with data:', slidesData.map(s => ({ name: s.name, type: s.type })))
    
    for (let i = 0; i < slidesData.length; i++) {
      const slideData = slidesData[i]
      
      // Create slide
      const { data: slide, error: slideError } = await supabase
        .from('slides')
        .insert([{
          project_id: projectId,
          slide_order: i,
          slide_type: slideData.type,
          background: slideData.background || { type: 'color', value: '#ffffff' },
          metadata: { 
            name: slideData.name || 'Untitled Slide',
            generatedImageId: slideData.metadata?.generatedImageId || null
          }
        }])
        .select()
        .single()

      if (slideError) {
        console.error('Error creating slide:', slideError)
        continue
      }

      // Create elements for this slide
      if (slideData.elements && slideData.elements.length > 0) {
        const elementsToCreate = slideData.elements.map(element => {
          const elementData = {
            slide_id: slide.id,
            element_type: element.type,
            style: element.style || {},
            position: element.style?.position || { x: 0, y: 0 },
            z_index: 0
          }

          // Handle different element types
          if (element.type === 'image') {
            const fallbackSrc = 'https://images.unsplash.com/photo-1566438480900-0609be27a4be?w=400&h=300&fit=crop'
            elementData.content = {
              src: element.src && element.src.startsWith('http') ? element.src : fallbackSrc,
              alt: element.alt || '',
              generatedImageId: element.generatedImageId || null
            }
          } else if (element.type === 'list') {
            // Normalize list content to array of strings
            let items = []
            if (Array.isArray(element.content)) {
              items = element.content
            } else if (element.content?.items && Array.isArray(element.content.items)) {
              items = element.content.items
            } else if (element.content?.text && Array.isArray(element.content.text)) {
              items = element.content.text
            } else if (typeof element.content === 'string') {
              // Split newline or comma-separated strings into items
              items = element.content
                .split(/\n|,/)
                .map(s => s.trim())
                .filter(Boolean)
            }
            elementData.content = { items }
          } else if (element.type === 'link') {
            elementData.content = {
              text: element.content,
              href: element.href || ''
            }
          } else {
            elementData.content = { text: element.content }
          }

          return elementData
        })

        const { error: elementsError } = await supabase
          .from('elements')
          .insert(elementsToCreate)

        if (elementsError) {
          console.error('Error creating elements:', elementsError)
        }
      }
    }
  }

  const handleCreate = async () => {
    if (!projectTitle.trim()) {
      alert('Please enter a project title')
      return
    }

    if (!user) {
      alert('You must be logged in to create a project')
      return
    }

    // Check credits for AI-powered options
    if (selectedOption === 'prompt' || selectedOption === 'script') {
      if (!credits || credits.projectCredits < 1) {
        alert('You need at least 1 AI Project Generation credit to create an AI-powered project. Please upgrade your plan or wait for your monthly quota to reset.')
        return
      }
    }

    setLoading(true)
    setProgressStep(0)
    // Initialize estimated countdown for AI flows (prompt/script)
    if (selectedOption === 'prompt' || selectedOption === 'script') {
      setEstimateSeconds(300) // ~5 minutes
      if (estimateTimerRef.current) clearInterval(estimateTimerRef.current)
      estimateTimerRef.current = setInterval(() => {
        setEstimateSeconds(prev => {
          if (prev <= 1) {
            clearInterval(estimateTimerRef.current)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      setEstimateSeconds(0)
    }

    try {
      let slides = []
      
      if (selectedOption === 'blank') {
        setProgressMessage('Creating blank slides...')
        setProgressStep(1)
        slides = createBlankSlides()
      } else if (selectedOption === 'prompt') {
        setProgressMessage('🤖 Analyzing your prompt with AI...')
        setProgressStep(1)
        
        // Add artificial delay to show progress for AI processing
        await new Promise(resolve => setTimeout(resolve, 1500))
        setProgressMessage('🎬 Generating professional slide content...')
        setProgressStep(2)
        
        // Consume project generation credit
        const creditConsumed = await creditService.consumeCredits(
          user.id, 
          'project', 
          1, 
          `AI project generation for "${projectTitle}"`,
          null // project ID not available yet
        )
        
        if (!creditConsumed) {
          throw new Error('Unable to consume project generation credit. Please try again.')
        }
        
        slides = await generateSlideFromPrompt(prompt, projectTitle, setProgressMessage, setProgressStep, user.id, advancedData)
      } else if (selectedOption === 'script') {
        setProgressMessage('📄 Reading and analyzing script...')
        setProgressStep(1)
        
        await new Promise(resolve => setTimeout(resolve, 1000))
        setProgressMessage('🎭 Extracting characters and story elements...')
        setProgressStep(2)
        
        // Consume project generation credit
        const creditConsumed = await creditService.consumeCredits(
          user.id, 
          'project', 
          1, 
          `AI project generation for "${projectTitle}"`,
          null // project ID not available yet
        )
        
        if (!creditConsumed) {
          throw new Error('Unable to consume project generation credit. Please try again.')
        }
        
        slides = await generateSlideFromScript(file, projectTitle, setProgressMessage, setProgressStep, user.id, advancedData)
      }

            // Generate AI content for AI-generated projects
      let projectMetadata = null
      let thumbnailImage = null
      
      if ((selectedOption === 'prompt' || selectedOption === 'script') && slides.length > 0) {
        // Generate project description and tags
        setProgressMessage('📝 Creating project description and tags...')
        setProgressStep(selectedOption === 'prompt' ? 3 : 3)
        
        try {
          projectMetadata = await aiService.generateProjectMetadata(
            selectedOption === 'script' ? `Script-based project: ${projectTitle}` : prompt,
            projectTitle,
            selectedOption
          )
          console.log('✅ Generated project metadata:', projectMetadata)
        } catch (error) {
          console.error('❌ Failed to generate project metadata:', error)
          // Use fallback metadata
          projectMetadata = {
            description: selectedOption === 'prompt' ? prompt : `Created from script: ${projectTitle}`,
            tags: ['professional', 'original', 'creative'],
            genre: 'Drama'
          }
        }

        // Generate cover image
        setProgressMessage('🎨 Generating cover poster with AI...')
        setProgressStep(selectedOption === 'prompt' ? 4 : 4)
        
        try {
          const coverSlide = slides[0] // First slide is usually the cover
          const synopsis = slides.find(s => s.name?.toLowerCase().includes('synopsis'))?.elements
            ?.find(e => e.type === 'text')?.content || prompt
          
          console.log('🎬 Generating cover image for:', projectTitle)
          
          const coverImage = await imageService.generateCoverImage({
            projectTitle,
            genre: projectMetadata?.genre || 'Drama',
            synopsis: typeof synopsis === 'string' ? synopsis : prompt,
            userId: user.id,
            projectId: null, // Will be set after project is created
            onProgress: (message) => {
              setProgressMessage(`🎨 ${message}`)
            }
          })

          // Update the cover slide background with the generated image
          if (coverSlide && coverImage) {
            coverSlide.background = {
              type: 'image',
              url: coverImage.url,
              position: 'center',
              size: 'cover'
            }
            // Store the generated image ID in slide metadata
            if (!coverSlide.metadata) coverSlide.metadata = {}
            coverSlide.metadata.generatedImageId = coverImage.id
            console.log('✅ Cover image generated and applied as background:', coverImage.url)
          }

        } catch (error) {
          console.error('❌ Failed to generate cover image:', error)
          setProgressMessage('⚠️ Cover image generation failed, continuing...')
          await new Promise(resolve => setTimeout(resolve, 1000))
        }

        // Generate thumbnail image
        setProgressMessage('🖼️ Creating project thumbnail...')
        setProgressStep(selectedOption === 'prompt' ? 5 : 5)
        
        try {
          thumbnailImage = await imageService.generateThumbnail({
            projectTitle,
            genre: projectMetadata?.genre || 'Drama',
            description: projectMetadata?.description || prompt,
            userId: user.id,
            projectId: null, // Will be set after project is created
            onProgress: (message) => {
              setProgressMessage(`🖼️ ${message}`)
            }
          })
          console.log('✅ Thumbnail generated:', thumbnailImage?.url)
        } catch (error) {
          console.error('❌ Failed to generate thumbnail:', error)
          setProgressMessage('⚠️ Thumbnail generation failed, continuing...')
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      // Create project in database
      setProgressMessage('💾 Creating project in database...')
      setProgressStep(selectedOption === 'blank' ? 2 : 6)
      
      const projectData = {
        title: projectTitle,
        description: projectMetadata?.description || (selectedOption === 'prompt' ? prompt : `Created from ${selectedOption}`),
        tags: projectMetadata?.tags || [],
        thumbnail_url: thumbnailImage?.url || null,
        status: 'draft'
      }

      const result = await createProject(projectData)
      
      if (result.error) {
        throw new Error(result.error.message)
      }

      const projectId = result.data.id

      // Save advanced project data if provided
      if ((selectedOption === 'prompt' || selectedOption === 'script') && advancedData && Object.keys(advancedData).length > 0) {
        try {
          console.log('💾 Saving advanced project data...')
          console.log('📊 Advanced data to save:', {
            hasCreator: !!advancedData.creator,
            hasOutline: !!advancedData.outline,
            projectType: advancedData.projectType,
            genre: advancedData.genre,
            yearSet: advancedData.yearSet,
            characterCount: advancedData.characters?.length || 0,
            locationCount: advancedData.locations?.length || 0,
            referenceImageCount: advancedData.referenceImages?.length || 0,
            hasExtraNotes: !!advancedData.extraNotes
          })
          const { error: advancedError } = await supabase
            .from('project_advanced_data')
            .insert([{
              project_id: projectId,
              creator_name: advancedData.creator,
              creator_company: advancedData.creatorCompany,
              project_outline: advancedData.outline,
              project_type: advancedData.projectType,
              year_set: advancedData.yearSet ? parseInt(advancedData.yearSet) : null,
              genre: advancedData.genre,
              locations: advancedData.locations || [],
              characters: advancedData.characters || [],
              extra_notes: advancedData.extraNotes,
              reference_image_ids: advancedData.referenceImages || []
            }])

          if (advancedError) {
            console.error('Error saving advanced project data:', advancedError)
            // Don't throw error - continue with project creation
          } else {
            console.log('✅ Advanced project data saved successfully')
          }
        } catch (error) {
          console.error('Error saving advanced project data:', error)
          // Don't throw error - continue with project creation
        }
      }

      // Create slides and elements in the database
      setProgressMessage('🎨 Building slide elements...')
      setProgressStep(selectedOption === 'blank' ? 3 : 7)
      await createSlidesInDatabase(projectId, slides)

      setProgressMessage('🎉 Finalizing your project...')
      setProgressStep(selectedOption === 'blank' ? 4 : 8)
      
      // Small delay to show completion
      await new Promise(resolve => setTimeout(resolve, 800))

      // Refresh credits after successful AI project creation
      if (selectedOption === 'prompt' || selectedOption === 'script') {
        await loadCredits()
      }
      
      // Navigate to editor
      navigate(`/editor/${projectId}`)
    } catch (error) {
      console.error('Error creating project:', error)
      alert('Error creating project: ' + error.message)
    } finally {
      setLoading(false)
      setProgressStep(0)
      setProgressMessage('')
      if (estimateTimerRef.current) {
        clearInterval(estimateTimerRef.current)
        estimateTimerRef.current = null
      }
    }
  }

  const createBlankSlides = () => {
    return [
      {
        id: 1,
        type: 'title',
        elements: [
          {
            id: 'title-1',
            type: 'text',
            content: projectTitle,
            style: {
              fontSize: '48px',
              fontWeight: 'bold',
              color: '#1f2937',
              textAlign: 'center',
              position: { x: 50, y: 200 },
              width: 700,
              height: 80
            }
          },
          {
            id: 'subtitle-1',
            type: 'text',
            content: 'Click to edit subtitle',
            style: {
              fontSize: '24px',
              color: '#6b7280',
              textAlign: 'center',
              position: { x: 50, y: 300 },
              width: 700,
              height: 40
            }
          }
        ],
        background: {
          type: 'gradient',
          value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }
      },
      {
        id: 2,
        type: 'content',
        elements: [
          {
            id: 'heading-2',
            type: 'text',
            content: 'Slide Title',
            style: {
              fontSize: '36px',
              fontWeight: 'bold',
              color: '#1f2937',
              position: { x: 50, y: 50 },
              width: 700,
              height: 50
            }
          },
          {
            id: 'content-2',
            type: 'text',
            content: 'Click to edit content...',
            style: {
              fontSize: '18px',
              color: '#374151',
              lineHeight: '1.6',
              position: { x: 50, y: 120 },
              width: 700,
              height: 200
            }
          }
        ],
        background: {
          type: 'color',
          value: '#ffffff'
        }
      },
      {
        id: 3,
        type: 'content',
        elements: [
          {
            id: 'heading-3',
            type: 'text',
            content: 'Another Slide',
            style: {
              fontSize: '36px',
              fontWeight: 'bold',
              color: '#1f2937',
              position: { x: 50, y: 50 },
              width: 700,
              height: 50
            }
          },
          {
            id: 'list-3',
            type: 'list',
            content: ['Point 1', 'Point 2', 'Point 3'],
            style: {
              fontSize: '18px',
              color: '#374151',
              lineHeight: '1.6',
              position: { x: 50, y: 120 },
              width: 700,
              height: 300
            }
          }
        ],
        background: {
          type: 'color',
          value: '#ffffff'
        }
      }
    ]
  }

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0]
    if (uploadedFile) {
      const allowedTypes = ['.txt', '.docx', '.pdf']
      const fileExtension = uploadedFile.name.toLowerCase().slice(uploadedFile.name.lastIndexOf('.'))
      
      if (allowedTypes.includes(fileExtension)) {
        setFile(uploadedFile)
      } else {
        alert('Please upload a .txt, .docx, or .pdf file')
      }
    }
  }

  const isReadyToCreate = () => {
    if (!projectTitle.trim()) return false
    if (selectedOption === 'blank') return true
    if (selectedOption === 'prompt') return prompt.trim().length > 0
    if (selectedOption === 'script') return file !== null
    return false
  }

  const getProgressSteps = () => {
    switch (selectedOption) {
      case 'blank':
        return [
          'Setting up project...',
          'Creating blank slides...',
          'Building slide elements...',
          'Finalizing project...'
        ]
      case 'prompt':
        return [
          'Analyzing your prompt...',
          'AI generating slide content...',
          'Creating project description & tags...',
          'Generating cover poster...',
          'Creating project thumbnail...',
          'Setting up project database...',
          'Building slide elements...',
          'Finalizing project...'
        ]
      case 'script':
        return [
          'Reading script file...',
          'AI analyzing script content...',
          'Creating project description & tags...',
          'Generating cover poster...',
          'Creating project thumbnail...',
          'Setting up project database...',
          'Building slide elements...',
          'Finalizing project...'
        ]
      default:
        return ['Creating project...']
    }
  }

  const getLoadingMessage = () => {
    return progressMessage || 'Processing...'
  }

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Create New Project</h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Choose how you'd like to start your slide deck. Each option offers different features to match your workflow.
              </p>
            </div>
            {!creditsLoading && credits && (
              <div className="flex-shrink-0 ml-8">
                <CreditDisplay compact={true} />
              </div>
            )}
          </div>
        </div>

        {/* Options */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {options.map((option) => (
            <div
              key={option.id}
              onClick={() => setSelectedOption(option.id)}
              className={`p-8 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                selectedOption === option.id
                  ? 'border-primary-500 bg-primary-50 shadow-xl scale-105'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg hover:scale-102'
              }`}
            >
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 ${
                option.color === 'blue' ? 'bg-blue-100' :
                option.color === 'purple' ? 'bg-purple-100' : 'bg-green-100'
              }`}>
                <SafeIcon icon={option.icon} className={`w-8 h-8 ${
                  option.color === 'blue' ? 'text-blue-600' :
                  option.color === 'purple' ? 'text-purple-600' : 'text-green-600'
                }`} />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3">{option.title}</h3>
              <p className="text-gray-600 mb-4">{option.description}</p>

              <ul className="space-y-2">
                {option.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {selectedOption === option.id && (
                <div className="mt-4 pt-4 border-t border-primary-200">
                  <div className="flex items-center space-x-2 text-primary-600">
                    <SafeIcon icon={FiCheck} className="w-5 h-5" />
                    <span className="font-medium">Selected</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Project Title */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Project Title *
          </label>
          <input
            type="text"
            value={projectTitle}
            onChange={(e) => setProjectTitle(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="e.g., Horror Film Pitch - The Haunted Manor"
          />
        </div>

        {/* Advanced Form - only show for prompt and script options */}
        {(selectedOption === 'prompt' || selectedOption === 'script') && (
          <AdvancedForm
            onDataChange={setAdvancedData}
            initialData={advancedData}
          />
        )}

        {/* Additional Input */}
        {selectedOption === 'prompt' && (
          <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-200 shadow-sm">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              Describe your project in detail
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., A psychological horror film about a family who moves into a Victorian mansion and discovers it's haunted by the spirits of its previous owners. The story explores themes of family trauma, redemption, and the supernatural. The film should have a dark, atmospheric tone with elements of mystery and suspense. Target audience: adults 18-35, fans of elevated horror like Hereditary and The Conjuring."
              className="w-full h-40 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <div className="flex justify-between items-center mt-4">
              <p className="text-sm text-gray-500">
                Include genre, themes, tone, target audience, and key story elements
              </p>
              <span className={`text-sm ${prompt.length > 100 ? 'text-green-600' : 'text-gray-400'}`}>
                {prompt.length} characters
              </span>
            </div>
          </div>
        )}

        {selectedOption === 'script' && (
          <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-200 shadow-sm">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              Upload your script file
            </label>
            <div className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
              file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-primary-300 hover:bg-primary-25'
            }`}>
              {file ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <SafeIcon icon={FiFileText} className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Remove file and choose another
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                    <SafeIcon icon={FiUpload} className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-lg text-gray-600 mb-2">
                      Drop your script file here or{' '}
                      <label className="text-primary-600 cursor-pointer hover:text-primary-700 font-medium">
                        browse files
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          accept=".txt,.docx,.pdf"
                          className="hidden"
                        />
                      </label>
                    </p>
                    <p className="text-sm text-gray-500">
                      Supports .txt, .docx, and .pdf files up to 10MB
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Button */}
        <div className="text-center">
          {!loading && (
            <>
              <button
                onClick={handleCreate}
                disabled={!isReadyToCreate()}
                className="bg-primary-600 text-white px-12 py-4 rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-3 mx-auto shadow-lg hover:shadow-xl"
              >
                <span>Create Project</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>

              {!isReadyToCreate() && (
                <p className="text-sm text-gray-500 mt-4">
                  {!projectTitle.trim() ? 'Enter a project title to continue' :
                   selectedOption === 'prompt' && !prompt.trim() ? 'Add a project description to continue' :
                   selectedOption === 'script' && !file ? 'Upload a script file to continue' :
                   'Select a creation option above'}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Progress Modal Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-2xl max-w-lg w-full mx-auto">
            {/* Progress Header */}
            <div className="mb-6 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Creating Your Project</h3>
              <p className="text-gray-600">{getLoadingMessage()}</p>
              {(selectedOption === 'prompt' || selectedOption === 'script') && (
                <p className="text-sm text-gray-500 mt-2">
                  Approximate time remaining: <span className="font-medium text-gray-700">{formatTime(estimateSeconds)}</span> (about 5 minutes)
                </p>
              )}
            </div>

            {/* Progress Steps */}
            <div className="space-y-4 mb-6">
              {getProgressSteps().map((step, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index < progressStep 
                      ? 'bg-green-500 text-white' 
                      : index === progressStep 
                      ? 'bg-primary-500 text-white animate-pulse' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {index < progressStep ? (
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <span className={`${
                    index < progressStep 
                      ? 'text-green-600 font-medium' 
                      : index === progressStep 
                      ? 'text-primary-600 font-medium' 
                      : 'text-gray-500'
                  }`}>
                    {step}
                  </span>
                </div>
              ))}
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${(progressStep / getProgressSteps().length) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-2 text-center">
                Step {progressStep} of {getProgressSteps().length}
              </p>
            </div>

            {/* Removed legacy AI processing note in favor of countdown timer */}
          </div>
        </div>
      )}
    </div>
  )
}

export default CreateProject